import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ICreateOrUpdateBoardDialogProps } from 'src/types/boards-lists';

export default function CreateOrUpdateBoardDialog({
  open,
  isEdit,
  initialValues,
  onClose,
  onSubmit,
}: ICreateOrUpdateBoardDialogProps) {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open && initialValues) {
      setTitle(initialValues.title || '');
      setSubtitle(initialValues.subtitle || '');
      setDescription(initialValues.description || '');
      setTags(initialValues.tags || []);
      setTagInput('');
    } else if (!open) {
      setTitle('');
      setSubtitle('');
      setDescription('');
      setTags([]);
      setTagInput('');
    }
  }, [open, initialValues]);

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
    setTagInput('');
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleSubmit = () => {
    if (!title.trim()) return;
    const payload = {
      title: title.trim(),
      subtitle: subtitle.trim(),
      description: description.trim(),
      tags,
    };

    if (isEdit && initialValues && (initialValues as any).id) {
      onSubmit({ ...payload, id: (initialValues as any).id });
    } else {
      onSubmit(payload);
    }
    setTitle('');
    setSubtitle('');
    setDescription('');
    setTags([]);
    setTagInput('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>{isEdit ? 'Edit Board' : 'Create New Board'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Subtitle"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          minRows={2}
        />
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Tags
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
              />
            ))}
          </Box>
          <Box display="flex" gap={1}>
            <TextField
              label="Add tag"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              size="small"
            />
            <Button
              onClick={handleAddTag}
              variant="outlined"
              disabled={!tagInput.trim()}
            >
              Add
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={
          fullScreen
            ? {
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1,
                px: 2,
                pb: 2,
                pt: 1,
              }
            : {}
        }
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={
            fullScreen
              ? {
                  width: '100%',
                  py: 2,
                  fontSize: 18,
                }
              : {}
          }
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!title.trim()}
          sx={
            fullScreen
              ? {
                  width: '100%',
                  py: 2,
                  fontSize: 18,
                }
              : {}
          }
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
